import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback } from 'react';
import debug from '@/utils/debug';
import { AuthenticationResult, AccountInfo, EventType, EventMessage } from '@azure/msal-browser';
import { apiRequest, defaultScopes, getAccessTokenSilently } from './authConfig';
import msalInstance from './msalInstance';
import axiosInstance from '../utils/axiosConfig'; // Import axios instance
import { useStore, getUserDefaults } from '../store/useStore'; // Import Zustand store and getUserDefaults
import { Client, UserProfile, StaffProfile } from '../store/types'; // Import Client type

const isBrowser = typeof window !== 'undefined';

type AuthContextType = {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  userProfile: UserProfile | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  isCheckingAuth: boolean;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  userProfile: null,
  login: async () => {},
  logout: async () => {},
  error: null,
  isCheckingAuth: true,
});

export const useAuth = () => useContext(AuthContext);

const authProviderDebug = debug.scope('AuthProvider');

// Helper function to determine user role based on email and profile
const determineUserRole = (email: string, profile: UserProfile): string => {
  const emailLower = email.toLowerCase();
  const domain = emailLower.split('@')[1] || '';
  
  // Check if user is hybrid staff
  if (profile.staff_profile?.is_hybrid_staff) {
    return 'executive'; // Hybrid staff get executive defaults
  }
  
  // Check email patterns for role detection
  if (emailLower.includes('marketing') || emailLower.includes('digital') || emailLower.includes('analytics')) {
    return 'marketing_analyst';
  }
  
  if (emailLower.includes('exec') || emailLower.includes('director') || emailLower.includes('head') || emailLower.includes('chief')) {
    return 'executive';
  }
  
  // Check for university domains (likely recruitment managers)
  const universityDomains = ['.ac.uk', '.edu', '.edu.au'];
  if (universityDomains.some(uniDomain => domain.endsWith(uniDomain))) {
    return 'recruitment_manager';
  }
  
  // Default role
  return 'default';
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  // Get Zustand setters
  const setAvailableClients = useStore((state) => state.setAvailableClients);
  const setCurrentClient = useStore((state) => state.setCurrentClient);
  const setUserPreferences = useStore((state) => state.setUserPreferences);
  const applyUserPreferences = useStore((state) => state.applyUserPreferences);

  const persistDjangoToken = useCallback((token: string) => {
    if (isBrowser) {
      localStorage.setItem('auth_token', token);
    }
    axiosInstance.defaults.headers.common.Authorization = `Token ${token}`;
  }, []);

  useEffect(() => {
    if (!isBrowser) return;
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      axiosInstance.defaults.headers.common.Authorization = `Token ${storedToken}`;
    }
  }, []);

  // Initialize auth state only on client-side
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        // Check if the required environment variables are set
        if (!process.env.NEXT_PUBLIC_AZURE_AD_CLIENT_ID || !process.env.NEXT_PUBLIC_AZURE_AD_TENANT_ID) {
          setError("Azure AD configuration is missing. Please check your environment variables.");
          setIsInitialized(true);
          return;
        }
        
        if (!msalInstance) {
          setError("MSAL instance failed to initialize.");
          setIsInitialized(true);
          return;
        }
        
        // Handle redirect promise
        msalInstance.handleRedirectPromise().then(response => {
          if (response) {
            handleResponse(response);
          }
        }).catch(err => {
          setError(`Redirect handling error: ${err.message}`);
        });
        
        // Set up event callbacks
        msalInstance.addEventCallback((event: EventMessage) => {
          if (event.eventType === EventType.LOGIN_SUCCESS) {
            const payload = event.payload as AuthenticationResult;
            handleResponse(payload);
          }
        });
        
        // Check if user is already signed in
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          setUser(accounts[0]);
          // Don't set authenticated until backend validates
          // setIsAuthenticated(true);
          // Instead, try to validate with backend
          validateExistingSession(accounts[0]);
        }
        
        setIsInitialized(true);
      } catch (err: any) {
        setError(`Auth initialization error: ${err.message}`);
        setIsInitialized(true);
      } finally {
        // If no accounts were found, we're not checking auth
        const accounts = msalInstance?.getAllAccounts() || [];
        if (accounts.length === 0) {
          setIsCheckingAuth(false);
        }
      } // closes catch block
    } // closes if (typeof window !== 'undefined')
  }, []); // Keep original dependency array

  // Fetch clients and set initial client once authenticated
  useEffect(() => {
    const initializeClientData = async () => {
      if (isAuthenticated && user) {
        try {
          authProviderDebug.log("Fetching available clients...");
          // Add cache-busting query parameter and correct the endpoint path
          const cacheBuster = `?_=${Date.now()}`;
          const response = await axiosInstance.get<Client[]>(`/api/clients/clients/${cacheBuster}`);
          let backendClients = Array.isArray(response.data) ? response.data : [];
          authProviderDebug.log("AuthProvider - Backend clients fetched:", backendClients); // Log backend clients
          
          // Load and merge temporary clients from localStorage
          let finalAvailableClients = backendClients;
          try {
            const tempClientsString = localStorage.getItem('tempClients');
            if (tempClientsString) {
              const tempClients = JSON.parse(tempClientsString);
              const tempClientsArray = Array.isArray(tempClients) ? tempClients : [];
              // Combine backend clients with temporary clients, ensuring no duplicates by ID
              const clientMap = new Map<string, Client>();
              backendClients.forEach(client => clientMap.set(client.id, client));
              tempClientsArray.forEach(client => clientMap.set(client.id, client)); // Overwrite backend client if temp exists with same ID
              finalAvailableClients = Array.from(clientMap.values());
              authProviderDebug.log("AuthProvider - Merged temporary clients from localStorage.");
            }
          } catch (e) {
            authProviderDebug.error("AuthProvider - Failed to load or merge temporary clients during initial load:", e);
            // Proceed with only backend clients if localStorage fails
          }
          
          authProviderDebug.log("AuthProvider - Final available clients before setting store:", finalAvailableClients); // Log final list
          setAvailableClients(finalAvailableClients);
          authProviderDebug.log("AuthProvider - Available clients set in store.");

          // Determine initial client from the final list
          let initialClient: Client | null = null;
          const lastClientId = localStorage.getItem('last_selected_client_id');

          if (lastClientId) {
            initialClient = finalAvailableClients.find(c => c.id === lastClientId) || null;
            if (initialClient) {
              authProviderDebug.log(`Found last selected client in localStorage: ${initialClient.name}`);
            } else {
              authProviderDebug.log(`Client ID ${lastClientId} from localStorage not found in available clients.`);
              localStorage.removeItem('last_selected_client_id'); // Clean up invalid ID
            }
          }

          // If no valid client from localStorage, determine default
          if (!initialClient) {
            const isHybridUser = userProfile?.staff_profile?.is_hybrid_staff || user.username.toLowerCase().endsWith('@hybrid.co');

            if (isHybridUser) {
              // Specific default for hybrid.co users
              initialClient = finalAvailableClients.find(c => c.name?.toLowerCase() === "demo") || null;
              if (initialClient) {
                authProviderDebug.log(`Setting default client for hybrid.co user: demo`);
              } else {
                 authProviderDebug.warn(`Default client "demo" not found for hybrid.co user.`);
                 // Fallback if demo doesn't exist
                 initialClient = finalAvailableClients.length > 0 ? finalAvailableClients[0] : null;
                 if(initialClient) authProviderDebug.log(`Falling back to first available client for hybrid.co user: ${initialClient.name}`);
              }
            } else {
              // Logic for non-hybrid users
              const usernameLower = user.username.toLowerCase();
              authProviderDebug.log("AuthProvider - User email:", user.username); // Log user email

              // Attempt to find a client based on university email domain
              const response = await axiosInstance.get('/api/university-mapping/');
              const universityClients = response.data;

            authProviderDebug.log("AuthProvider - University client mappings:", universityClients); // Log university client mappings

            let foundUniversityClient: Client | null = null;
            for (const uni of universityClients) {
              if (usernameLower.endsWith(uni.domain)) {
                authProviderDebug.log(`AuthProvider - Email domain match found: ${uni.domain}`); // Log domain match
                foundUniversityClient = finalAvailableClients.find(c => c.name?.trim().toLowerCase() === uni.clientName.toLowerCase()) || null;
                if (foundUniversityClient) {
                  authProviderDebug.log(`AuthProvider - Found university client by name: ${foundUniversityClient.name}`); // Log found client
                  authProviderDebug.log(`Setting client based on university email domain (${uni.domain}): ${foundUniversityClient.name}`);
                  initialClient = foundUniversityClient; // Set initialClient here
                  break; // Found a match, no need to check others
                } else {
                   authProviderDebug.warn(`Client "${uni.clientName}" not found for user with domain ${uni.domain}.`);
                 }
               }
             }

              // If no university client found, fall back to default UK/USA logic
              if (!initialClient) { // Check initialClient, which would be set if a university client was found
                authProviderDebug.log("AuthProvider - No university client found, falling back to default UK/USA logic."); // Log fallback
                const isUKUser = usernameLower.endsWith('.co.uk') || usernameLower.endsWith('.ac.uk'); // Check for both commercial and academic UK domains
                const defaultClientName = isUKUser ? "demo" : "Default US";

                initialClient = finalAvailableClients.find(c => c.name?.toLowerCase() === defaultClientName.toLowerCase()) || null;
                if (initialClient) {
                  authProviderDebug.log(`AuthProvider - Setting default client based on user region: ${initialClient.name}`); // Log default client
                } else {
                   authProviderDebug.warn(`AuthProvider - Default client "${defaultClientName}" not found in available clients.`); // Log warning if default client not found
                   // Fallback: maybe select the first available client? Or leave as null?
                   initialClient = finalAvailableClients.length > 0 ? finalAvailableClients[0] : null;
                   if(initialClient) authProviderDebug.log(`AuthProvider - Falling back to first available client: ${initialClient.name}`); // Log fallback to first client
                }
              }
            }
          }

          authProviderDebug.log("AuthProvider - Final initialClient:", initialClient?.name || 'None'); // Log the final selected client name

          setCurrentClient(initialClient);

        } catch (err: any) {
          authProviderDebug.error("Error fetching or setting client data:", err);
          setError(`Failed to load client configurations: ${err.message}`);
          // Handle error appropriately, maybe set clients to empty array
          setAvailableClients([]);
          setCurrentClient(null);
        }
      } else {
         // Avoid clearing persisted selection while auth is still being determined
         if (isCheckingAuth) {
           authProviderDebug.log('Auth check in progress, skipping client reset.');
           return;
         }
         // If user logs out, clear client data and user profile
         setAvailableClients([]);
         setCurrentClient(null);
         setUserProfile(null);
         if (typeof window !== 'undefined') {
            localStorage.removeItem('last_selected_client_id');
         }
      }
    };

    initializeClientData();
  }, [isAuthenticated, isCheckingAuth, user, userProfile, setAvailableClients, setCurrentClient, setUserPreferences, applyUserPreferences]); // Rerun when auth state changes

  // Function to validate existing session with backend
  const validateExistingSession = async (account: AccountInfo) => {
    setIsCheckingAuth(true);
    try {
      const email = account.username;
      authProviderDebug.log(`Validating existing session for ${email}...`);
      const storedToken = isBrowser ? localStorage.getItem('auth_token') : null;
      if (storedToken) {
        persistDjangoToken(storedToken);
      }
      
      // Try to validate with backend
      const response = await axiosInstance.get('/api/auth-test/');
      if (response.data) {
        authProviderDebug.log('Session validated successfully');
        setIsAuthenticated(true);
        
        // Fetch user profile
        const profile = await fetchUserProfile(email);
        if (profile) {
          setUserProfile(profile);
          
          // Apply user defaults
          const userRole = determineUserRole(email, profile);
          const userDefaults = getUserDefaults(userRole, email);
          setUserPreferences(userDefaults);
          applyUserPreferences(userDefaults);
        }
      } else {
        authProviderDebug.log('Session validation failed');
        setIsAuthenticated(false);
      }
    } catch (error) {
      authProviderDebug.error('Error validating session:', error);
      setIsAuthenticated(false);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  // Function to fetch user profile with staff information
  const fetchUserProfile = async (email: string): Promise<UserProfile | null> => {
    try {
      authProviderDebug.log(`Fetching user profile for: ${email}`);
      const response = await axiosInstance.get('/api/auth-test/');
      
      if (response.data) {
        const userProfile: UserProfile = {
          id: 0, // Will be set from backend if available
          username: response.data.user || email,
          email: response.data.email || email,
          staff_profile: undefined // Will be populated if staff data exists
        };

        // Only hybrid staff accounts need the staff profile lookup
        if (email.toLowerCase().endsWith('@hybrid.co')) {
          try {
            const staffResponse = await axiosInstance.get('/api/clients/staff-profile/');
            if (staffResponse.data) {
              userProfile.staff_profile = staffResponse.data;
            }
          } catch (staffError) {
            authProviderDebug.log('No staff profile found or error fetching staff profile:', staffError);
            // Staff profile is optional, continue without it
          }
        }

        return userProfile;
      }
      return null;
    } catch (error) {
      authProviderDebug.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Make handleResponse async to call the backend
  const handleResponse = async (response: AuthenticationResult) => {
    if (response.account) {
      setUser(response.account);
      // Don't set authenticated until Django validates
      setError(null);
      setIsCheckingAuth(true);

      // Step 5: Call Django backend to get token after MSAL login
      const email = response.account.username; // MSAL often uses 'username' for the email/UPN
      if (email) {
        try {
          authProviderDebug.log(`MSAL login successful for ${email}. Requesting Django token...`);
          const djangoLoginResponse = await axiosInstance.post('/api/login/', { email });

          if (djangoLoginResponse.data && djangoLoginResponse.data.token) {
            const djangoToken = djangoLoginResponse.data.token;
            await axiosInstance.post('/api/set-auth-cookie/', { token: djangoToken });
            authProviderDebug.log('Successfully set Django token in HttpOnly cookie.');
            persistDjangoToken(djangoToken);
            
            // NOW set authenticated after Django validates
            setIsAuthenticated(true);
            
            // Fetch user profile with staff information
            const profile = await fetchUserProfile(email);
            if (profile) {
              setUserProfile(profile);
              authProviderDebug.log('User profile loaded:', profile);
              
              // Apply user role-based defaults
              // Since role is not in StaffProfile, determine role from email/profile
              const userRole = determineUserRole(email, profile);
              const userDefaults = getUserDefaults(userRole, email);
              setUserPreferences(userDefaults);
              applyUserPreferences(userDefaults);
              authProviderDebug.log('Applied user defaults based on role:', userRole, userDefaults);
            }
            
            // Optionally store other user info if needed
            // localStorage.setItem('user_id', djangoLoginResponse.data.user_id);
            // localStorage.setItem('user_email', djangoLoginResponse.data.email);
          } else {
            authProviderDebug.error('Django login response missing token:', djangoLoginResponse.data);
            setError('Failed to retrieve authentication token from backend.');
            setIsAuthenticated(false);
            // Consider logging out or showing a more specific error
          }
        } catch (djangoError: any) {
          authProviderDebug.error('Error calling Django login endpoint:', djangoError);
          setError(`Failed to complete backend login: ${djangoError.response?.data?.error || djangoError.message}`);
          setIsAuthenticated(false);
          // Consider logging out MSAL session if backend login fails critically
          // await logout();
        } finally {
          setIsCheckingAuth(false);
        }
      } else {
        authProviderDebug.error('Could not extract email from MSAL account info.');
        setError('Failed to get user email after login.');
        setIsAuthenticated(false);
        setIsCheckingAuth(false);
      }
    } else {
       authProviderDebug.warn('MSAL response did not contain account information.');
       setError('Login process did not return user account details.');
       setIsCheckingAuth(false);
    }
  };

  const login = async (): Promise<void> => {
    if (!msalInstance) {
      setError("MSAL instance not initialized");
      return;
    }
    
    try {
      setError(null);
      await msalInstance.loginRedirect({
        scopes: defaultScopes,
      });
    } catch (err: any) {
      setError(`Login error: ${err.message}`);
    }
  };

  const logout = async (): Promise<void> => {
    if (!msalInstance) {
      setError("MSAL instance not initialized");
      return;
    }
    
    try {
      // Use logoutRedirect with the postLogoutRedirectUri from config
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: typeof window !== 'undefined' ? 
          `${window.location.origin}/login?message=You%20have%20been%20logged%20out` : ''
      });
      setIsAuthenticated(false);
      setUser(null);
      setUserProfile(null);
      if (isBrowser) {
        localStorage.removeItem('auth_token');
      }
      delete axiosInstance.defaults.headers.common.Authorization;
    } catch (err: any) {
      setError(`Logout error: ${err.message}`);
    }
  };



  // Only render the authentication context when on client side and initialized
  if (typeof window === 'undefined' || !isInitialized) {
    return <>{children}</>;
  }

  const authContextValue: AuthContextType = {
    isAuthenticated,
    user,
    userProfile,
    login,
    logout,
    error,
    isCheckingAuth,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
